// ========================================
// Stock Treemap - ויזואליזציית שווי מלאי
// ========================================
import React, { useMemo } from 'react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import { calculateNetStock, calculateDOI, getTrafficLight, formatNumber } from '../utils/calculations';

// צבעים לפי סטטוס
const STATUS_COLORS = {
    red: '#ef4444',
    yellow: '#eab308',
    green: '#22c55e',
};

/**
 * Treemap של שווי המלאי
 * גודל = מלאי פיזי (או שווי אם יש UnitCost)
 * צבע = סטטוס רמזור (אדום/צהוב/ירוק)
 */
const StockTreemap = ({ items, forecastFactor = 0, onItemClick }) => {
    // הכנת הנתונים ל-Treemap
    const treemapData = useMemo(() => {
        if (!items || items.length === 0) return [];

        const processed = items
            .map(item => {
                const netStock = calculateNetStock(item);
                const doi = calculateDOI(netStock, item.AvgDailySales, forecastFactor);
                const status = getTrafficLight(netStock, doi);

                // שווי = מלאי פיזי * עלות (אם יש), אחרת מלאי פיזי בלבד
                const value = item.UnitCost
                    ? Math.abs(item.PhysicalStock * item.UnitCost)
                    : Math.abs(item.PhysicalStock);

                return {
                    name: item.SKU,
                    size: Math.max(value, 1), // מינימום 1 כדי שייראה
                    status,
                    color: STATUS_COLORS[status],
                    description: item.Description,
                    physicalStock: item.PhysicalStock,
                    doi,
                    classification: item.Classification,
                    originalItem: item,
                };
            })
            .filter(item => item.size > 0)
            .sort((a, b) => b.size - a.size); // מהגדול לקטן

        return [{
            name: 'Inventory',
            children: processed,
        }];
    }, [items, forecastFactor]);

    // Custom Content לכל משבצת
    const CustomizedContent = (props) => {
        const { x, y, width, height, name, color, status } = props;

        // לא להציג משבצות קטנות מדי
        if (width < 30 || height < 20) return null;

        return (
            <g>
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    style={{
                        fill: color,
                        stroke: '#fff',
                        strokeWidth: 2,
                        cursor: 'pointer',
                        opacity: 0.85,
                    }}
                    onClick={() => {
                        const item = treemapData[0]?.children?.find(i => i.name === name);
                        if (item && onItemClick) {
                            onItemClick(item.originalItem);
                        }
                    }}
                />
                {width > 60 && height > 35 && (
                    <text
                        x={x + width / 2}
                        y={y + height / 2}
                        textAnchor="middle"
                        fill="#fff"
                        fontSize={width > 100 ? 12 : 10}
                        fontWeight="bold"
                        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                    >
                        {name}
                    </text>
                )}
            </g>
        );
    };

    // Custom Tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-right min-w-[180px]" dir="rtl">
                    <p className="font-bold text-gray-900 mb-1">{data.name}</p>
                    {data.description && (
                        <p className="text-xs text-gray-500 mb-2 line-clamp-2">{data.description}</p>
                    )}
                    <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-gray-500">מלאי:</span>
                            <span className="font-semibold">{formatNumber(data.physicalStock)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">DOI:</span>
                            <span className="font-semibold">{data.doi > 999 ? '∞' : data.doi}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">סיווג:</span>
                            <span className="font-semibold">{data.classification}</span>
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="text-gray-500">סטטוס:</span>
                            <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: data.color }}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    if (!items || items.length === 0) {
        return (
            <div className="flex items-center justify-center h-64 text-gray-400">
                אין נתונים להצגה
            </div>
        );
    }

    // חישוב סטטיסטיקות
    const stats = useMemo(() => {
        const children = treemapData[0]?.children || [];
        return {
            red: children.filter(c => c.status === 'red').length,
            yellow: children.filter(c => c.status === 'yellow').length,
            green: children.filter(c => c.status === 'green').length,
            total: children.length,
        };
    }, [treemapData]);

    return (
        <div className="space-y-4">
            {/* Summary Stats */}
            <div className="flex justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-gray-600">קריטי: <strong>{stats.red}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-yellow-500" />
                    <span className="text-gray-600">נמוך: <strong>{stats.yellow}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-gray-600">תקין: <strong>{stats.green}</strong></span>
                </div>
            </div>

            {/* Treemap */}
            <div className="h-[400px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                        data={treemapData}
                        dataKey="size"
                        aspectRatio={4 / 3}
                        stroke="#fff"
                        fill="#8884d8"
                        content={<CustomizedContent />}
                    >
                        <Tooltip content={<CustomTooltip />} />
                    </Treemap>
                </ResponsiveContainer>
            </div>

            {/* Legend */}
            <p className="text-center text-xs text-gray-500">
                לחץ על משבצת לצפייה בפרטי הפריט. גודל המשבצת = כמות המלאי.
            </p>
        </div>
    );
};

export default StockTreemap;
